import { REPORT_HEADER_SHELL } from "../../helpers/reportHeader";
import { getInvoiceTotalPresentation } from "../../helpers/invoiceReport";
import {
  buildInvoicePartiesLayout,
  getVisibleInvoiceSectionSubtotals,
  resolveInvoiceLegalNamePresentation,
} from "../../helpers/invoiceReportLayout";
import { formatCurrency } from "../../helpers/estimateTotals";
import {
  PDF_COLORS,
  PDF_PAGE_MARGINS,
  PDF_STYLES,
  pdfGoldRule,
} from "../shared/pdfTheme";
import {
  buildNotesSection,
  buildInvoiceFooterSection,
  buildPaymentInstructionsSection,
  buildStandardFooter,
  buildTotalsPanel,
  totalsRow,
} from "../shared/pdfDocumentParts";

function buildInvoiceBrandHeader({ logoDataUrl, branding, docTitle }) {
  const brandName = branding?.brandName || "Organization";
  const legal = resolveInvoiceLegalNamePresentation(
    brandName,
    branding?.legalNameLine,
  );

  const identityStack = [{ text: brandName, style: "brandName" }];
  if (legal) {
    identityStack.push({
      text: legal.text,
      style: "companyTagline",
      color:
        legal.emphasis === "subtle" ? PDF_COLORS.muted : PDF_COLORS.text,
      fontSize: legal.emphasis === "subtle" ? 7.5 : 8.5,
      margin: [0, 2, 0, 0],
    });
  }
  (branding?.registrationLines || []).forEach((line) => {
    identityStack.push({
      text: line,
      style: "companyTagline",
      fontSize: 7.5,
      margin: [0, 2, 0, 0],
    });
  });

  const leftStack = [];
  if (logoDataUrl) {
    leftStack.push({
      columns: [
        { image: logoDataUrl, width: 44, height: 44 },
        {
          stack: identityStack,
          margin: [10, 2, 0, 0],
          width: "*",
        },
      ],
      columnGap: 0,
    });
  } else {
    leftStack.push({ stack: identityStack });
  }

  const contactStack = (branding?.contactLines || []).map((line) => ({
    text: line,
    style: "companyTagline",
    alignment: "right",
    margin: [0, 0, 0, 1],
  }));

  const brandRow =
    contactStack.length > 0
      ? {
          columns: [
            { stack: leftStack, width: "*" },
            { stack: contactStack, width: 200 },
          ],
          columnGap: 20,
        }
      : { stack: leftStack };

  return [
    brandRow,
    {
      text: String(docTitle || "Invoice").toUpperCase(),
      style: "docTitle",
      alignment: "left",
      characterSpacing: 2.5,
      fontSize: 16,
      margin: [0, 14, 0, 8],
    },
    pdfGoldRule(),
  ];
}

function buildBillToColumn(billTo) {
  const stack = [
    {
      text: billTo.title,
      style: "panelTitle",
      margin: [0, 0, 0, 6],
    },
  ];

  if (billTo.name) {
    stack.push({
      text: billTo.name,
      style: "itemName",
      margin: [0, 0, 0, 3],
    });
  }

  billTo.lines.forEach((line) => {
    stack.push({
      text: line,
      style: "itemDetail",
      margin: [0, 0, 0, 1],
    });
  });

  return { stack, width: "*" };
}

function buildDetailsColumn(details) {
  const stack = [
    {
      text: details.title,
      style: "panelTitle",
      margin: [0, 0, 0, 6],
    },
  ];

  details.rows.forEach((row) => {
    stack.push({
      columns: [
        {
          width: 78,
          text: `${row.label}:`,
          style: "metaLabel",
        },
        {
          width: "*",
          text: row.value,
          style: "metaValue",
        },
      ],
      columnGap: 6,
      margin: [0, 0, 0, 3],
    });
  });

  return { stack, width: "*" };
}

function buildInvoicePartiesBlock(fields) {
  const parties = buildInvoicePartiesLayout(fields);
  const hasBillTo =
    Boolean(parties.billTo.name) || parties.billTo.lines.length > 0;
  const hasDetails = parties.details.rows.length > 0;

  if (!hasBillTo && !hasDetails) return [];

  const columns = [];
  if (hasBillTo) columns.push(buildBillToColumn(parties.billTo));
  if (hasDetails) columns.push(buildDetailsColumn(parties.details));

  return [
    {
      columns,
      columnGap: 28,
      margin: [0, 4, 0, 12],
    },
  ];
}

function buildInvoiceItemRow(row) {
  const nameStack = [
    { text: row.name, style: "itemName", margin: [0, 0, 0, 2] },
  ];

  if (row.description) {
    nameStack.push({
      text: row.description,
      style: "itemDetail",
      margin: [0, 0, 0, 2],
    });
  }

  if (row.date) {
    nameStack.push({ text: row.date, style: "itemDetail" });
  }

  if (row.detail) {
    nameStack.push({
      text: row.detail,
      style: "itemDetail",
      margin: [0, 2, 0, 0],
    });
  }

  if (row.notes) {
    nameStack.push({
      text: `Notes: ${row.notes}`,
      style: "itemDetail",
      margin: [0, 2, 0, 0],
    });
  }

  const columns = [{ width: "*", stack: nameStack }];

  if (row.amount != null) {
    columns.push({
      width: 72,
      text: formatCurrency(row.amount),
      style: "itemAmount",
      alignment: "right",
    });
  }

  return {
    columns,
    columnGap: 12,
    margin: [0, 4, 0, 4],
  };
}

function buildInvoiceSection(title, section) {
  if (!section || section.mode === "hidden") return [];

  const content = [
    pdfGoldRule(),
    {
      text: title,
      style: "sectionTitle",
      alignment: "left",
      margin: [0, 2, 0, 8],
    },
  ];

  if (section.mode === "summary") {
    content.push(totalsRow(section.summaryLabel, section.subtotal));
    return content;
  }

  section.rows.forEach((row) => {
    content.push(buildInvoiceItemRow(row));
  });

  content.push(
    totalsRow(
      `${section.summaryLabel} Subtotal`,
      section.subtotal,
      "totalsSubtotal",
    ),
  );

  return content;
}

function buildInvoiceGrandTotalSection(reportModel, presentation) {
  if (!presentation) return [];

  const rows = [];
  const sectionSubtotals = getVisibleInvoiceSectionSubtotals(reportModel);

  sectionSubtotals.forEach((row) => {
    rows.push(totalsRow(row.label, row.amount));
  });

  if (presentation.mode === "withTax") {
    if (
      presentation.lineSubtotal != null &&
      sectionSubtotals.length !== 1
    ) {
      rows.push(totalsRow("Line Subtotal", presentation.lineSubtotal));
    }

    if (Number(presentation.projectDiscountPercent) > 0) {
      rows.push(
        totalsRow(
          `Project Discount (${presentation.projectDiscountPercent}%)`,
          presentation.afterProjectDiscount,
        ),
      );
    }

    if (Number(presentation.goodwillAdjustment) !== 0) {
      rows.push(
        totalsRow("Goodwill Adjustment", presentation.goodwillAdjustment),
      );
    }

    rows.push(totalsRow("Taxable Subtotal", presentation.subtotal));
    presentation.lines.forEach((line) => {
      rows.push(totalsRow(line.label || "Tax", line.amount));
    });
  }

  rows.push({
    columns: [
      { width: "*", text: presentation.totalLabel, style: "totalsGrand" },
      {
        width: 90,
        text: formatCurrency(presentation.total),
        style: "totalsGrand",
        alignment: "right",
      },
    ],
    margin: [0, 4, 0, 0],
  });

  return [pdfGoldRule(), buildTotalsPanel(rows)];
}

export function buildInvoicePdfDocumentDefinition(
  reportModel,
  { logoDataUrl, branding } = {},
) {
  if (!reportModel) {
    throw new Error("Report model is required");
  }

  const { header, notes, paymentInstructions, paymentTerms, customerNotes, invoiceFooter } =
    reportModel;
  const totalPresentation = getInvoiceTotalPresentation(reportModel);
  const orderedSections = Array.isArray(reportModel.sectionsOrdered)
    ? reportModel.sectionsOrdered
    : [
        {
          id: "services",
          title: "Services",
          section: reportModel.sections?.services,
        },
        { id: "labour", title: "Labour", section: reportModel.sections?.labour },
        {
          id: "materials",
          title: "Materials",
          section: reportModel.sections?.materials,
        },
      ];

  const customerNotesText = customerNotes?.notes || notes || null;

  const content = [
    ...buildInvoiceBrandHeader({
      logoDataUrl,
      branding,
      docTitle: REPORT_HEADER_SHELL.invoice.docTitle,
    }),
    ...buildInvoicePartiesBlock(header?.fields || []),
    ...orderedSections.flatMap(({ title, section }) =>
      buildInvoiceSection(title, section),
    ),
    ...(totalPresentation
      ? buildInvoiceGrandTotalSection(reportModel, totalPresentation) || []
      : []),
    ...(buildPaymentInstructionsSection(paymentInstructions) || []),
    ...(buildPaymentInstructionsSection(paymentTerms) || []),
    ...(buildNotesSection(
      customerNotesText,
      customerNotes?.title || "Customer Notes",
    ) || []),
    ...(buildInvoiceFooterSection(invoiceFooter) || []),
  ];

  return {
    info: {
      title: `Invoice — ${header?.invoiceNumber || "Draft"}`,
      author: branding?.pdfAuthor || "Organization",
      subject: header?.projectName || "Invoice",
    },
    pageSize: "LETTER",
    pageOrientation: "portrait",
    pageMargins: PDF_PAGE_MARGINS,
    defaultStyle: {
      font: "Roboto",
      fontSize: 9,
      color: PDF_COLORS.text,
      lineHeight: 1.25,
    },
    styles: PDF_STYLES,
    footer: buildStandardFooter(
      header?.generatedDate,
      branding?.footerText || "Organization",
    ),
    content,
  };
}
